import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { systemClock } from '@sakhtar/calendar';
import {
  V2Card,
  V2CardHeader,
  V2Badge,
  V2EmptyState,
  LoadingState,
  StatePanel,
  Icon,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPatch } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { formatRialFa, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  AccountListItem,
  AccountListResponse,
  AccountNormalBalance,
  Dimension,
  DimensionRuleListResponse,
  TrialBalanceResponse,
} from '../../lib/types/finance.js';
import { NORMAL_BALANCE_LABEL } from '../../lib/types/finance.js';
import { apiPut } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { LEVEL_LABELS } from './CoaTree.js';
import { levelChipStyle } from './CoaTreeGrid.js';

function depthOf(account: AccountListItem, byId: Map<string, AccountListItem>): number {
  let depth = 0;
  let current: AccountListItem | undefined = account;
  while (current?.parentAccountId) {
    current = byId.get(current.parentAccountId);
    depth += 1;
    if (depth > 10) break; // guard against a malformed cycle — never trust raw depth alone
  }
  return depth;
}

function balanceLabel(
  row: { debitBalanceRial: string; creditBalanceRial: string } | undefined,
  unit: AmountDisplayUnit,
) {
  if (!row) return '—';
  const debit = BigInt(row.debitBalanceRial);
  const credit = BigInt(row.creditBalanceRial);
  if (debit > 0n) return `${formatRialFa(row.debitBalanceRial, unit)} بد`;
  if (credit > 0n) return `${formatRialFa(row.creditBalanceRial, unit)} بس`;
  return '۰';
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §05 "ACCOUNT TREE · DESKTOP · 1000px"
 *   (per-row مانده column) — this codebase's own AccountsPage.tsx tree
 *   doesn't have a per-account detail route at all (ERP-199).
 * archetype: ENTITY_DETAIL
 * v2 finance frame (2026-10): section-card headers carry the module icon chip
 *   and the level chip reuses the COA tree's `levelChipStyle` (CoaTreeGrid.tsx)
 *   so a row and its detail page read the same; the v2 finance frame has no
 *   dedicated account-detail artboard, so no further layout is invented.
 * status: PARTIAL_REAL — `GET .../finance/accounts/:accountId`
 *   (`apps/api/src/accounting/coa.controller.ts:84`) returns exactly the
 *   same fields as a list row (no richer detail data exists server-side),
 *   so this page composes it with two other real, already-used endpoints
 *   for real added value: the full account list (to resolve the parent
 *   and direct children via `parentAccountId` — the same tree the list
 *   page already builds) and today's trial balance (to show the account's
 *   real current balance, matching the design's tree balance column —
 *   `GET .../finance/reports/trial-balance`, ERP-168). The design's
 *   "ACCOUNT IN USE · NO DELETE" frame is correctly not built: no delete
 *   endpoint exists anywhere for an account (only create + set-postable),
 *   so there is no destructive action here to guard in the first place.
 * state reference: ready · loading · not_found · permission_denied ·
 *   network_error
 */
export function AccountDetailPage() {
  const { unit } = useAmountDisplay();
  const { slug, accountId } = useParams<{ slug: string; accountId: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [refreshKey, setRefreshKey] = useState(0);
  const [postableBusy, setPostableBusy] = useState(false);
  const [ruleBusy, setRuleBusy] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const rules = useApiGet<DimensionRuleListResponse>(
    slug && accountId ? `${base}/api/v1/finance/dimension-rules?accountId=${accountId}` : null,
    [slug, accountId, refreshKey],
  );
  const dimensions = useApiGet<{ readonly items: readonly Dimension[] }>(
    slug ? `${base}/api/v1/master-data/dimensions?status=active&limit=200` : null,
    [slug],
  );

  const account = useApiGet<AccountListItem>(
    slug && accountId ? `${base}/api/v1/finance/accounts/${accountId}` : null,
    [slug, accountId, refreshKey],
  );
  const list = useApiGet<AccountListResponse>(slug ? `${base}/api/v1/finance/accounts` : null, [
    slug,
    refreshKey,
  ]);
  const today = systemClock.today();
  const trialBalance = useApiGet<TrialBalanceResponse>(
    slug ? `${base}/api/v1/finance/reports/trial-balance?asOfDate=${today}` : null,
    [slug, today],
  );

  const byId = useMemo(() => {
    const map = new Map<string, AccountListItem>();
    if (list.status === 'ready') {
      for (const item of list.data.items) map.set(item.accountId, item);
    }
    return map;
  }, [list]);

  const parent =
    account.status === 'ready' && account.data.parentAccountId
      ? (byId.get(account.data.parentAccountId) ?? null)
      : null;
  const children =
    list.status === 'ready' && account.status === 'ready'
      ? list.data.items
          .filter((i) => i.parentAccountId === account.data.accountId)
          .sort((a, b) => a.code.localeCompare(b.code))
      : [];
  const balanceRow =
    trialBalance.status === 'ready' && account.status === 'ready'
      ? trialBalance.data.rows.find((r) => r.accountRef === account.data.code)
      : undefined;

  if (!slug || !accountId) return null;

  async function onTogglePostable() {
    if (account.status !== 'ready') return;
    setPostableBusy(true);
    try {
      await apiPatch(`${base}/api/v1/finance/accounts/${encodeURIComponent(account.data.code)}`, {
        isPostable: !account.data.isPostable,
      });
      setRefreshKey((k) => k + 1);
    } finally {
      setPostableBusy(false);
    }
  }

  async function onNormalBalance(normalBalance: AccountNormalBalance) {
    if (account.status !== 'ready') return;
    setAccountError(null);
    try {
      await apiPatch(`${base}/api/v1/finance/accounts/${encodeURIComponent(account.data.code)}`, {
        normalBalance,
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setAccountError(bannerMessageFrom(err, 'تغییر ماهیت حساب ناموفق بود.'));
    }
  }

  async function setRule(dimensionId: string, status: 'active' | 'inactive', required: boolean) {
    if (account.status !== 'ready') return;
    setAccountError(null);
    setRuleBusy(dimensionId);
    try {
      await apiPut(
        `${base}/api/v1/finance/accounts/${encodeURIComponent(account.data.code)}/dimension-rules/${dimensionId}`,
        { status, required },
      );
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setAccountError(bannerMessageFrom(err, 'تغییر سطح تفصیلی ناموفق بود.'));
    } finally {
      setRuleBusy(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <Link
          to={`${base}/finance/accounts`}
          style={{ fontSize: 13, color: 'var(--sk-fg-muted)', textDecoration: 'none' }}
        >
          ← بازگشت به کدینگ حساب‌ها
        </Link>
      </div>

      {account.status === 'loading' ? <LoadingState /> : null}
      {account.status === 'error' ? (
        <StatePanel
          variant={
            account.error.status === 403
              ? 'permission_denied'
              : account.error.status === 404
                ? 'not_found'
                : 'network_error'
          }
          title={
            account.error.status === 403
              ? 'دسترسی ندارید'
              : account.error.status === 404
                ? 'حسابی با این شناسه پیدا نشد'
                : 'حساب قابل نمایش نیست'
          }
        />
      ) : null}

      {account.status === 'ready' ? (
        <>
          <V2Card>
            <V2CardHeader
              icon="layers"
              title={account.data.name}
              subtitle={
                <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                  {account.data.code}
                </span>
              }
              actions={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={levelChipStyle(depthOf(account.data, byId))}>
                    {LEVEL_LABELS[Math.min(depthOf(account.data, byId), LEVEL_LABELS.length - 1)]}
                  </span>
                  <V2Badge tone={account.data.status === 'active' ? 'success' : 'neutral'}>
                    {account.data.status === 'active' ? 'فعال' : 'غیرفعال'}
                  </V2Badge>
                </div>
              }
            />
            <div
              style={{
                padding: '16px 20px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 14,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>حساب والد</span>
                {parent ? (
                  <Link to={`${base}/finance/accounts/${parent.accountId}`}>
                    {parent.code} — {parent.name}
                  </Link>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--sk-fg-subtle)' }}>
                    ریشه — بدون والد
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>قابل ثبت سند</span>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  disabled={postableBusy}
                  onClick={() => void onTogglePostable()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: 'fit-content',
                    fontSize: 13,
                    color: account.data.isPostable ? 'var(--sk-success)' : 'var(--sk-fg-subtle)',
                  }}
                >
                  {account.data.isPostable ? <Icon name="check" size="sm" /> : null}
                  {account.data.isPostable ? 'قابل ثبت' : 'غیرقابل ثبت'}
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>ماهیت حساب</span>
                <select
                  className="sk-select"
                  aria-label="ماهیت حساب"
                  value={account.data.normalBalance ?? 'either'}
                  onChange={(e) => void onNormalBalance(e.target.value as AccountNormalBalance)}
                  style={{ width: 'fit-content' }}
                >
                  {(Object.keys(NORMAL_BALANCE_LABEL) as AccountNormalBalance[]).map((nb) => (
                    <option key={nb} value={nb}>
                      {nb === 'either' ? 'مهم نیست' : `${NORMAL_BALANCE_LABEL[nb]}‌بس`}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                  مانده — تا امروز، فقط اسناد قطعی
                </span>
                {trialBalance.status === 'loading' ? (
                  <span style={{ fontSize: 13, color: 'var(--sk-fg-subtle)' }}>در حال محاسبه…</span>
                ) : (
                  <span className="sk-num" style={{ fontSize: 15, fontWeight: 700 }}>
                    {balanceLabel(balanceRow, unit)}
                  </span>
                )}
              </div>
            </div>
          </V2Card>

          {accountError ? (
            <div style={{ fontSize: 13, color: 'var(--sk-danger)' }}>{accountError}</div>
          ) : null}

          {account.data.isPostable ? (
            <V2Card>
              <V2CardHeader
                icon="tag"
                title="سطوح تفصیلی شناور"
                subtitle="مرکز هزینه، پروژه و … که در سطرهای سند روی این حساب قابل انتخاب است"
              />
              {dimensions.status === 'ready' && dimensions.data.items.length === 0 ? (
                <V2EmptyState
                  icon="info"
                  title="سطح تفصیلی تعریف نشده"
                  description="ابتدا در «مراکز هزینه و پروژه‌ها» سطح تفصیلی بسازید."
                  compact
                />
              ) : null}
              {dimensions.status === 'ready' && dimensions.data.items.length > 0 ? (
                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th>سطح تفصیلی</th>
                        <th style={{ width: 140 }}>مجاز در این حساب</th>
                        <th style={{ width: 140 }}>الزامی</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dimensions.data.items.map((d) => {
                        const rule =
                          rules.status === 'ready'
                            ? rules.data.items.find((r) => r.dimensionId === d.dimensionId)
                            : undefined;
                        const active = rule?.status === 'active';
                        return (
                          <tr key={d.dimensionId}>
                            <td>{d.name}</td>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`مجاز بودن ${d.name}`}
                                checked={active}
                                disabled={ruleBusy === d.dimensionId}
                                onChange={(e) =>
                                  void setRule(
                                    d.dimensionId,
                                    e.target.checked ? 'active' : 'inactive',
                                    false,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`الزامی بودن ${d.name}`}
                                checked={active && rule?.required === true}
                                disabled={!active || ruleBusy === d.dimensionId}
                                onChange={(e) =>
                                  void setRule(d.dimensionId, 'active', e.target.checked)
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                    «الزامی» فقط برای حساب‌هایی که اسنادشان دستی ثبت می‌شود مناسب است؛ اسناد خودکار
                    (فروش، خرید، خزانه) سطح تفصیلی ارسال نمی‌کنند.
                  </div>
                </div>
              ) : null}
            </V2Card>
          ) : null}

          <V2Card>
            <V2CardHeader icon="layers" title="زیرحساب‌ها" />
            {children.length === 0 ? (
              <V2EmptyState icon="database" title="این حساب زیرحسابی ندارد" compact />
            ) : (
              <div className="sk-table-scroll">
                <table className="sk-table">
                  <thead>
                    <tr>
                      <th>کد</th>
                      <th>نام</th>
                      <th style={{ width: 130 }}>قابل ثبت</th>
                      <th style={{ width: 130 }}>وضعیت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((child) => (
                      <tr key={child.accountId}>
                        <td className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
                          <Link to={`${base}/finance/accounts/${child.accountId}`}>
                            {child.code}
                          </Link>
                        </td>
                        <td>
                          <Link to={`${base}/finance/accounts/${child.accountId}`}>
                            {child.name}
                          </Link>
                        </td>
                        <td>{child.isPostable ? 'قابل ثبت' : 'غیرقابل ثبت'}</td>
                        <td>
                          <V2Badge tone={child.status === 'active' ? 'success' : 'neutral'}>
                            {child.status === 'active' ? 'فعال' : 'غیرفعال'}
                          </V2Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </V2Card>
        </>
      ) : null}
    </div>
  );
}
